/**
 * Cookie expiry config from backend (dev_env/prod_env). Fetched once; getters return cached or defaults.
 */

import { api } from './authService';

const DEFAULTS = {
  auth_cookie_expire_days: 7,
  selected_organization_cookie_expire_days: 365,
  organization_cache_expire_minutes: 5,
  dashboard_cache_minutes: 60,
  admin_users_list_cache_minutes: 10,
  genie_spaces: [],
};

let cached = null;

/**
 * Fetch config from GET /api/v2/config. Call early (e.g. App.js useEffect). Uses shared api client (401/403 → login).
 */
export async function fetchCookieConfig() {
  try {
    const { data } = await api.get('/api/v2/config');
    cached = {
      auth_cookie_expire_days: data.auth_cookie_expire_days ?? DEFAULTS.auth_cookie_expire_days,
      selected_organization_cookie_expire_days: data.selected_organization_cookie_expire_days ?? DEFAULTS.selected_organization_cookie_expire_days,
      organization_cache_expire_minutes: data.organization_cache_expire_minutes ?? DEFAULTS.organization_cache_expire_minutes,
      dashboard_cache_minutes: data.dashboard_cache_minutes ?? DEFAULTS.dashboard_cache_minutes,
      admin_users_list_cache_minutes: data.admin_users_list_cache_minutes ?? DEFAULTS.admin_users_list_cache_minutes,
      genie_spaces: Array.isArray(data.genie_spaces) ? data.genie_spaces : DEFAULTS.genie_spaces,
    };
  } catch {
    // keep cached or null (use defaults)
  }
  return getCookieConfig();
}

/**
 * Current config (cached from fetch or defaults).
 */
export function getCookieConfig() {
  return cached ? { ...cached } : { ...DEFAULTS };
}

/** AuthContext: user cookie expiry in days */
export function getAuthCookieExpireDays() {
  return (cached ?? DEFAULTS).auth_cookie_expire_days;
}

/** Organization: selected_organization cookie expiry in days */
export function getSelectedOrganizationCookieExpireDays() {
  return (cached ?? DEFAULTS).selected_organization_cookie_expire_days;
}

/** Organization: treat org list as fresh for this many minutes before refetch */
export function getOrganizationCacheExpireMinutes() {
  return (cached ?? DEFAULTS).organization_cache_expire_minutes;
}

/** Dashboard: chart and count cookie expiry in minutes */
export function getDashboardCacheMinutes() {
  return (cached ?? DEFAULTS).dashboard_cache_minutes;
}

/** Dashboard: chart and count cookie expiry in days (for setCookie). */
export function getDashboardCacheDays() {
  return getDashboardCacheMinutes() / (60 * 24);
}

/** Admin: User Management users list cookie cache duration in minutes */
export function getAdminUsersListCacheMinutes() {
  return (cached ?? DEFAULTS).admin_users_list_cache_minutes;
}

/**
 * Chat: ordered list of selectable Genie spaces from backend (`{key, label}`); empty array
 * before fetchCookieConfig completes.
 */
export function getGenieSpaces() {
  const spaces = (cached ?? DEFAULTS).genie_spaces;
  return Array.isArray(spaces) ? spaces : [];
}
